import { createDeployPluginHeadless } from '../../_shared/common/createDeployPluginHeadless';
import { APP_INSTANCE_CAPABILITIES } from '../capabilities';
import { APP_INSTANCE_FIELD_KEYS } from '../fields';
import { APP_INSTANCE_MODULES } from '../modules';
import { buildAppInstanceDeliveryMetadata, buildAppInstanceOrderDeliveryMetadata } from './deliveryMetadata';
import { filterAppInstanceSpecByEnabledModules, isAppInstanceModuleEnabled } from './enabledModules';
import { appInstanceMigrations, normalizeAppInstancePresetToSpec } from './migrations';
import {
  formToPayload,
  getAppStorageSubmitError,
  prepareApplicationInstanceSubmit,
  readApplicationInstanceFormValues,
  readApplicationInstanceSpecFromForm,
  validateAppStorage,
} from './payload';
import {
  buildAppInstanceDisplayDescriptors,
  buildAppInstanceResourceConfigs,
  pickAppInstancePresetFields,
  serializeAppInstanceApiBody,
} from './projections';
import { type AppInstanceApiPayload, type AppInstanceSpec, AppInstanceSpecSchema } from './schema';
import { appInstanceFromForm, appInstanceGetInitialFormValues, appInstanceToForm } from './spec';

const appInstanceBaseHeadless = createDeployPluginHeadless<
  AppInstanceSpec,
  AppInstanceApiPayload,
  (typeof APP_INSTANCE_FIELD_KEYS)[number]
>({
  code: 'AppInstance',
  fieldKeys: APP_INSTANCE_FIELD_KEYS,
  modules: APP_INSTANCE_MODULES,
  capabilities: APP_INSTANCE_CAPABILITIES,
  schema: AppInstanceSpecSchema,
  fromForm: appInstanceFromForm,
  toForm: appInstanceToForm,
  getInitialFormValues: appInstanceGetInitialFormValues,
  projectionProfile: {
    profile: 'sku-capable',
    projections: {
      apiBody: serializeAppInstanceApiBody,
      skuPreset: pickAppInstancePresetFields,
      buildDisplayDescriptors: buildAppInstanceDisplayDescriptors,
      skuResources: buildAppInstanceResourceConfigs,
    },
  },
  migrations: appInstanceMigrations,
});

export const appInstancePipelineKernel = appInstanceBaseHeadless.pipeline;

/** SKU preset（扁平表单或完整 Spec）规范化后再 parse */
export const appInstancePipeline = {
  ...appInstancePipelineKernel,
  parseSpec: (input: unknown) => appInstancePipelineKernel.parseSpec(normalizeAppInstancePresetToSpec(input)),
};

/**
 * Headless 描述（已 registerDeployPlugin；可经 getDeployPluginHeadless('AppInstance') 获取）。
 */
export const appInstanceHeadless = {
  ...appInstanceBaseHeadless,
  pipeline: appInstancePipeline,
};

export {
  buildAppInstanceDeliveryMetadata,
  buildAppInstanceOrderDeliveryMetadata,
  filterAppInstanceSpecByEnabledModules,
  formToPayload,
  getAppStorageSubmitError,
  isAppInstanceModuleEnabled,
  prepareApplicationInstanceSubmit,
  readApplicationInstanceFormValues,
  readApplicationInstanceSpecFromForm,
  validateAppStorage,
};
